import React, { useState } from "react";
import { db } from "../firebase";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";
import { useApp } from "../App";
import { IconBack, IconHeart, IconWhatsApp, IconEdit, IconDelete, IconFlag, IconCheck } from "../components/Icons";

export default function ListingDetail({ listing, onBack, onUpdated }) {
  const { user, t } = useApp();
  const [saved, setSaved] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmSold, setShowConfirmSold] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editPrice, setEditPrice] = useState(String(listing.price));
  const [editNote, setEditNote] = useState(listing.note || "");
  const [editLocation, setEditLocation] = useState(listing.location || "");
  const [saving, setSaving] = useState(false);

  const isOwner = user && listing.userId === user.uid;
  const isSold = listing.status === "sold";

  const LOCATIONS = [
    { group: "Cascais line", options: [
      "Cascais","Estoril","Birre / Areia / Tires","São Domingos de Rana",
      "Alcabideche","Parede","Carcavelos","São João do Estoril",
    ]},
    { group: "Oeiras", options: [
      "Oeiras","Miraflores","Algés","Porto Salvo","Barcarena",
    ]},
    { group: "Lisbon", options: [
      "Belém / Restelo","Príncipe Real / Estrela","Campo de Ourique / Lapa",
      "Chiado / Bairro Alto","Avenidas Novas","Parque das Nações",
    ]},
    { group: "Sintra / North", options: [
      "Sintra","Queluz","Amadora",
    ]},
    { group: "Other", options: [
      "Other side of river","Outside Lisbon",
    ]},
  ];

  const formatPrice = (price) => {
    if (price === 0 || price === "0") return "Free";
    return `€${price}`;
  };

  const conditionLabel = {
    likeNew: "Like new",
    good: "Good condition",
    used: "Well used",
  };

  const paymentLabel = {
    mbway: "MBWay",
    cash: "Cash",
    both: "MBWay or cash",
  };

  const handleContact = () => {
    const number = (listing.userWhatsApp || "").replace(/\D/g, "");
    const msg = encodeURIComponent(
      `Hi! I'm interested in your listing on kepti market: "${listing.title}" (${formatPrice(listing.price)}). Is it still available?`
    );
    window.open(`https://wa.me/${number}?text=${msg}`, "_blank");
  };

  const handleMarkSold = async () => {
    const ref = doc(db, "market_listings", listing.id);
    await updateDoc(ref, { status: "sold" });
    onUpdated({ ...listing, status: "sold" });
    setShowConfirmSold(false);
  };

  const handleDelete = async () => {
    await deleteDoc(doc(db, "market_listings", listing.id));
    onBack();
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    const ref = doc(db, "market_listings", listing.id);
    const updates = {
      price: parseFloat(editPrice) || 0,
      note: editNote.trim(),
      location: editLocation,
    };
    await updateDoc(ref, updates);
    onUpdated({ ...listing, ...updates });
    setSaving(false);
    setShowEdit(false);
  };

  const handleFlag = async () => {
    const ref = doc(db, "market_listings", listing.id);
    await updateDoc(ref, { flagged: true });
    alert("Thanks for reporting. We'll review this listing.");
  };

  const toggleSave = async () => {
    setSaved((prev) => !prev);
  };

  return (
    <div className="detail-page">
      {/* Header */}
      <div className="detail-header">
        <button className="detail-back-btn" onClick={onBack}>
          <IconBack />
          Back
        </button>
        <button onClick={toggleSave}>
          <IconHeart filled={saved} />
        </button>
      </div>

      {/* Image */}
      {listing.imageUrl ? (
        <img className="detail-image" src={listing.imageUrl} alt={listing.title} />
      ) : (
        <div className="detail-image" style={{ background: "var(--offwhite)" }} />
      )}

      {isSold && (
        <div style={{ background: "var(--navy)", padding: "10px 20px", textAlign: "center" }}>
          <span style={{ color: "var(--cream)", fontWeight: 600, fontSize: 14 }}>
            This item has been sold
          </span>
        </div>
      )}

      <div className="detail-body">
        {/* Title & price */}
        <div className="detail-title">{listing.title}</div>
        <div className="detail-price">{formatPrice(listing.price)}</div>

        {/* Badges */}
        <div className="detail-badges">
          {listing.condition && (
            <span className={`detail-badge condition-${listing.condition}`}>
              {conditionLabel[listing.condition]}
            </span>
          )}
          {listing.payment && (
            <span className="detail-badge">{paymentLabel[listing.payment]}</span>
          )}
          {listing.location && (
            <span className="detail-badge">📍 {listing.location}</span>
          )}
          {listing.size && (
            <span className="detail-badge">Size: {listing.size}</span>
          )}
          {listing.movingSale && (
            <span className="detail-badge yellow">{t.movingSale}</span>
          )}
        </div>

        {/* Note */}
        {listing.note ? (
          <div className="detail-note">{listing.note}</div>
        ) : null}

        {/* Seller */}
        <div className="detail-seller">
          <div className="seller-avatar">
            {(listing.userName || "?")[0].toUpperCase()}
          </div>
          <div>
            <div className="seller-name">{listing.userName}</div>
            <div className="seller-label">{t.postedBy}</div>
          </div>
        </div>

        {/* Contact button — not shown to owner or if sold */}
        {!isOwner && !isSold && (
          <button className="contact-btn" onClick={handleContact}>
            <IconWhatsApp />
            {t.contactSeller}
          </button>
        )}

        {/* Owner actions */}
        {isOwner && !isSold && (
          <>
            <button
              className="mark-sold-btn"
              onClick={() => setShowConfirmSold(true)}
            >
              <IconCheck />
              {t.markAsSold}
            </button>
            <div className="detail-action-row">
              <button
                className="detail-action-btn"
                onClick={() => setShowEdit(true)}
              >
                <IconEdit />
                {t.editListing}
              </button>
              <button
                className="detail-action-btn danger"
                onClick={() => setShowConfirmDelete(true)}
              >
                <IconDelete />
                {t.deleteListing}
              </button>
            </div>
          </>
        )}

        {/* Flag */}
        {!isOwner && (
          <button className="detail-flag-btn" onClick={handleFlag}>
            <IconFlag />
            {t.flagListing}
          </button>
        )}
      </div>

      {/* Confirm sold modal */}
      {showConfirmSold && (
        <div className="modal-overlay" onClick={() => setShowConfirmSold(false)}>
          <div className="modal-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">{t.markAsSold}</div>
            <div className="modal-text">{t.confirmSold}</div>
            <div className="modal-btn-row">
              <button className="modal-btn secondary" onClick={() => setShowConfirmSold(false)}>
                Cancel
              </button>
              <button className="modal-btn primary" onClick={handleMarkSold}>
                Mark as sold
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete modal */}
      {showConfirmDelete && (
        <div className="modal-overlay" onClick={() => setShowConfirmDelete(false)}>
          <div className="modal-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">{t.deleteListing}</div>
            <div className="modal-text">{t.confirmDelete}</div>
            <div className="modal-btn-row">
              <button className="modal-btn secondary" onClick={() => setShowConfirmDelete(false)}>
                Cancel
              </button>
              <button className="modal-btn danger" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {showEdit && (
        <div className="modal-overlay" onClick={() => setShowEdit(false)}>
          <div className="modal-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">{t.editListing}</div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">{t.price}</label>
              <input
                className="form-input"
                type="number"
                inputMode="decimal"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                min="0"
              />
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">{t.location}</label>
              <select
                className="form-select"
                value={editLocation}
                onChange={(e) => setEditLocation(e.target.value)}
              >
                <option value="">{t.selectLocation}</option>
                {LOCATIONS.map((group) => (
                  <optgroup key={group.group} label={group.group}>
                    {group.options.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">{t.description}</label>
              <textarea
                className="form-textarea"
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                style={{ minHeight: 80 }}
              />
            </div>

            <div className="modal-btn-row">
              <button className="modal-btn secondary" onClick={() => setShowEdit(false)}>
                Cancel
              </button>
              <button className="modal-btn primary" onClick={handleSaveEdit} disabled={saving}>
                {saving ? t.saving : t.saveChanges}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
